import { useState, useEffect } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../firebase/config'
import { isUserEditor, isUserAdmin } from '../firebase/auth'
import { subscribeToEditorsNotes } from '../firebase/editorsNotes'
import './EditorsNote.css'

function formatDate(value) {
  if (!value) return '—'
  const date = value?.toDate ? value.toDate() : new Date(value)
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const EditorsNote = ({ user }) => {
  const [isEditor, setIsEditor] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [notes, setNotes] = useState([])
  const [generating, setGenerating] = useState(false)
  const [message, setMessage] = useState(null)
  const [copyId, setCopyId] = useState(null)

  useEffect(() => {
    const check = async () => {
      if (user) {
        const [editorStatus, adminStatus] = await Promise.all([
          isUserEditor(user),
          isUserAdmin(user)
        ])
        setIsEditor(editorStatus)
        setIsAdmin(adminStatus)
      }
      setLoading(false)
    }
    check()
  }, [user])

  useEffect(() => {
    if (isEditor || isAdmin) {
      const unsubscribe = subscribeToEditorsNotes(setNotes)
      return () => unsubscribe()
    }
  }, [isEditor, isAdmin])

  const handleGenerate = async () => {
    setMessage(null)
    setGenerating(true)
    try {
      const generateEditorsNote = httpsCallable(functions, 'generateEditorsNote')
      const result = await generateEditorsNote({})
      const data = result?.data ?? {}
      if (data.success && data.text) {
        setMessage({ type: 'success', text: "Editor's note generated and saved to history." })
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to generate note.' })
      }
    } catch (err) {
      const msg = err?.message || err?.code || 'Request failed.'
      setMessage({ type: 'error', text: msg })
    } finally {
      setGenerating(false)
    }
  }

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopyId(id)
      setTimeout(() => setCopyId(null), 2000)
    } catch {
      setMessage({ type: 'error', text: 'Copy failed.' })
    }
  }

  if (loading) {
    return (
      <div className="editors-note-container">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isEditor && !isAdmin) {
    return (
      <div className="editors-note-container">
        <div className="editors-note-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  const latestNote = notes[0]

  return (
    <div className="editors-note-container">
      <div className="editors-note-header">
        <h1>Editor&apos;s Note</h1>
        <p className="editors-note-subtitle">
          Generate AI security newsletter editor&apos;s notes with Claude. Each run is saved to history and can be copied to clipboard.
        </p>
      </div>

      <div className="editors-note-actions">
        <button
          type="button"
          className="editors-note-btn-generate"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate editor\'s note'}
        </button>
        {message && (
          <p className={`editors-note-message editors-note-message--${message.type}`}>
            {message.text}
          </p>
        )}
      </div>

      {latestNote && (
        <section className="editors-note-current">
          <div className="editors-note-current-header">
            <h2>Latest note</h2>
            <span className="editors-note-date">{formatDate(latestNote.createdAt)}</span>
            <button
              type="button"
              className="editors-note-btn-copy"
              onClick={() => copyToClipboard(latestNote.text, latestNote.id)}
              title="Copy to clipboard"
            >
              {copyId === latestNote.id ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="editors-note-text">{latestNote.text}</div>
        </section>
      )}

      {notes.length > 0 && (
        <section className="editors-note-history">
          <h2>History</h2>
          <ul className="editors-note-history-list">
            {notes.map((note) => (
              <li key={note.id} className="editors-note-history-item">
                <div className="editors-note-history-item-header">
                  <span className="editors-note-date">{formatDate(note.createdAt)}</span>
                  <button
                    type="button"
                    className="editors-note-btn-copy editors-note-btn-copy--small"
                    onClick={() => copyToClipboard(note.text, note.id)}
                    title="Copy to clipboard"
                  >
                    {copyId === note.id ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div className="editors-note-history-item-text">{note.text}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {notes.length === 0 && !generating && (
        <p className="editors-note-empty">No editor&apos;s notes yet. Click &quot;Generate editor&apos;s note&quot; to create one.</p>
      )}
    </div>
  )
}

export default EditorsNote
